/**
 * Exchange rates, and converting a stored amount from one currency to another.
 *
 * A rate is held as an integer: 1 unit of the "from" currency is
 * `rateE9 / 1_000_000_000` units of the "to" currency. Amounts everywhere in
 * this app are integer hundredths of a major unit (see currency.ts), so a
 * conversion is `cents * rateE9 / 1e9`, worked out in BigInt so that neither
 * the product nor the division ever passes through a float — a float would
 * turn €1,234.56 at 1.0834 into a number that rounds differently on two
 * machines.
 *
 * The result lands on the target currency's step (whole yen, whole forints),
 * rounding half away from zero like the pricing engine, so a converted amount
 * is always one that currency can actually be charged or recorded in.
 */

import { currencyStepCents, normalizeCurrencyCode } from './currency.ts';

export const RATE_SCALE = 1_000_000_000n;

/** The largest rate accepted: 1 unit of anything is at most this many of another. */
export const MAX_RATE_E9 = 10_000_000n * RATE_SCALE;

/**
 * A decimal rate as typed ("1.0834", "0.00072") into its integer form, or null
 * when it is not a positive number with at most nine decimal places.
 */
export function parseRate(input: string): number | null {
  const text = input.trim();
  const match = /^(\d{1,8})(?:\.(\d{1,9}))?$/.exec(text);
  if (!match) return null;
  const whole = BigInt(match[1]!);
  const fraction = BigInt((match[2] ?? '').padEnd(9, '0'));
  const rate = whole * RATE_SCALE + fraction;
  if (rate <= 0n || rate > MAX_RATE_E9) return null;
  return Number(rate);
}

/** The integer form back as the shortest decimal that means the same rate. */
export function formatRate(rateE9: number): string {
  const rate = BigInt(rateE9);
  const whole = rate / RATE_SCALE;
  const fraction = (rate % RATE_SCALE).toString().padStart(9, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

/** Half away from zero for an exact integer quotient `numerator / denominator`. */
function divideRounded(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const quotient = (n * 2n + d) / (d * 2n);
  return negative ? -quotient : quotient;
}

/**
 * Converts stored cents in one currency into stored cents in another, landing
 * on the target currency's step. `rateE9` is how many of `to` one `from` buys.
 */
export function convertCents(cents: number, rateE9: number, toCurrency: string): number {
  const step = BigInt(currencyStepCents(toCurrency));
  const converted = divideRounded(BigInt(cents) * BigInt(rateE9), RATE_SCALE * step) * step;
  return Number(converted);
}

/** The rate the other way round: how many of `from` one `to` buys. */
export function invertRate(rateE9: number): number {
  return Number(divideRounded(RATE_SCALE * RATE_SCALE, BigInt(rateE9)));
}

/**
 * The rate from `from` to `to`, given each currency's rate against one base
 * currency (the company's). The base itself is 1. Null when either side has
 * no rate yet — a conversion nobody has a rate for is not guessed at.
 */
export function crossRate(
  from: string,
  to: string,
  base: string,
  ratesToBase: ReadonlyMap<string, number>,
): number | null {
  const fromCode = normalizeCurrencyCode(from);
  const toCode = normalizeCurrencyCode(to);
  const baseCode = normalizeCurrencyCode(base);
  if (fromCode === toCode) return Number(RATE_SCALE);
  const fromRate = fromCode === baseCode ? Number(RATE_SCALE) : ratesToBase.get(fromCode);
  const toRate = toCode === baseCode ? Number(RATE_SCALE) : ratesToBase.get(toCode);
  if (fromRate === undefined || toRate === undefined) return null;
  return Number(divideRounded(BigInt(fromRate) * RATE_SCALE, BigInt(toRate)));
}

/**
 * A rate as somebody typed it, in the form `parseRate` reads. "1,25" is how a
 * rate is written in much of the world, so a comma standing alone is the
 * decimal mark; with both marks present, the last one is, and the other
 * groups thousands. Spaces (a thousands mark in some locales) go.
 */
export function normalizeTypedRate(input: string): string {
  const text = input.trim().replace(/[\s\u00a0\u202f]/g, '');
  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  if (lastComma === -1) return text;
  if (lastDot === -1) return text.replace(/,/g, (_, offset: number) => (offset === lastComma ? '.' : ''));
  return lastComma > lastDot ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '');
}
